<template>
	<Skeleton :paragraph="{ rows: 8 }" :title="false" :loading="loading">
		<VerticalLayout size="large">
			<VerticalLayout>
				<LabeledSwitch
					v-model="subNetworkEnabled"
					:disabled="settings.deleted"
					:rightLabel="$t('settings.sub-networks.vrsSettingsForm.vrsMode.' + (subNetworkEnabled ? 'enabled' : 'disabled'))"
					data-testid="settings-subnetwork-enabled-switch"
					@change="onSubNetworkEnabledChange"
				/>
				<LabeledSwitch
					v-if="licenseAllowVrs"
					v-model="vrsServiceEnabled"
					:disabled="settings.deleted"
					:rightLabel="$t('settings.sub-networks.vrsSettingsForm.subNetworkVrsEnabled.' + (vrsServiceEnabled ? 'enabled' : 'disabled'))"
					data-testid="settings-subnetwork-vrs-switch"
					@change="onUpdateField('ivrsEnabled', $event)"
				/>
				<LabeledSwitch
					v-if="licenseAllowPpp"
					v-model="pppServiceEnabled"
					:disabled="settings.deleted"
					:rightLabel="$t('settings.sub-networks.vrsSettingsForm.subNetworkPppEnabled.' + (pppServiceEnabled ? 'enabled' : 'disabled'))"
					data-testid="settings-subnetwork-ppp-switch"
					@change="onUpdateField('cssrBrdcEnabled', $event)"
				/>
			</VerticalLayout>
			<TypographyParagraph v-if="licenseAllowPpp && !pppServiceEnabled && !canEnablePpp">
				{{ $t('settings.vrsSettingsForm.pppRtk.requirements1') }}
				<router-link :to="{ name: 'settings-type', params: { id: 'external-sources' } }">
					{{ $t('settings.vrsSettingsForm.pppRtk.requirements2') }}
				</router-link>
			</TypographyParagraph>
			<div v-if="settings.statusOnline === 'broken' && !settings.deleted && settings.status !== 'stopped'" class="flex-wrapper align-baseline">
				<WarningTwoTone :two-tone-color="PINK" class="pd-sm-x" />
				<TypographyText>{{ $t('settings.sub-networks.vrsSettingsForm.brokenSubNetworkHint') }}</TypographyText>
			</div>
			<Descriptions
				v-if="!settings.deleted"
				:column="1"
				:colon="false"
				bordered
				size="small"
				:labelStyle="{ backgroundColor: 'white' }"
				:contentStyle="{ backgroundColor: 'white' }"
			>
				<DescriptionsItem v-if="licenseAllowPpp" :label="$t('settings.sub-networks.vrsSettingsForm.cssrTrafficStatusLabel')">
					<StatusDisplay
						:source="settingsRef"
						field="cssrTrafficStatus"
						prefix="settings.sub-networks.vrsSettingsForm.cssrTrafficStatus"
						:badgeDict="trafficStatusBadgeDict"
					/>
				</DescriptionsItem>
				<DescriptionsItem v-if="licenseAllowPpp" :label="$t('settings.sub-networks.vrsSettingsForm.brdcTrafficStatusLabel')">
					<StatusDisplay
						:source="settingsRef"
						field="brdcTrafficStatus"
						prefix="settings.sub-networks.vrsSettingsForm.brdcTrafficStatus"
						:badgeDict="trafficStatusBadgeDict"
					/>
				</DescriptionsItem>
				<DescriptionsItem v-if="licenseAllowPpp">
					<template #label>
						<StoragePathTooltip
							:storagePath="storagePath"
							:relativePath="cssrBrdcDumpTooltipPath"
							:label="$t('settings.sub-networks.vrsSettingsForm.cssrBrdcDumpEnabled')"
						/>
					</template>
					<HorizontalLayout>
						<LabeledSwitch
							v-model="cssrBrdcDumpEnabled"
							small
							:disabled="settings.deleted"
							@change="onDumpChange"
						/>
						<div :class="{ 'dump-restart-button-hidden': !dumpRestartRequired }">
							<Tooltip v-if="settings.status === 'stopped'" :title="$t('settings.sub-networks.vrsSettingsForm.restartDisabledTooltip')">
								<span>
									<PopconfirmButton
										size="small"
										:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
										:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
										:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
										:disabled="!dumpRestartRequired || settings.deleted || settings.status === 'stopped'"
										:onConfirm="onRestartConfirm"
									>
										{{ $t('settings.sub-networks.vrsSettingsForm.restartDumpButtonLabel') }}
									</PopconfirmButton>
								</span>
							</Tooltip>
							<PopconfirmButton
								v-else
								size="small"
								:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
								:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
								:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
								:disabled="!dumpRestartRequired || settings.deleted || settings.status === 'stopped'"
								:onConfirm="onRestartConfirm"
							>
								{{ $t('settings.sub-networks.vrsSettingsForm.restartDumpButtonLabel') }}
							</PopconfirmButton>
						</div>
					</HorizontalLayout>
				</DescriptionsItem>
				<DescriptionsItem v-if="licenseAllowVrs && vrsServiceEnabled" :label="$t('settings.sub-networks.vrsSettingsForm.finalNamesVrs')">
					{{ finalNamesVrs }}
				</DescriptionsItem>
				<DescriptionsItem v-if="licenseAllowPpp && pppServiceEnabled" :label="finalNameCssrLabel">
					{{ settingsRef.finalNameCssr }}
				</DescriptionsItem>
				<DescriptionsItem v-if="licenseAllowPpp && pppServiceEnabled" :label="$t('settings.sub-networks.vrsSettingsForm.finalNameBrdc')">
					{{ settingsRef.finalNameBrdc }}
				</DescriptionsItem>
			</Descriptions>
			<SettingsInfoBanner v-if="dumpRestartRequired && licenseAllowPpp" status="warning" class="pd-md-vertical">
				{{ $t('settings.sub-networks.vrsSettingsForm.restartRequiredAfterCssrBrdcDumpChange') }}
			</SettingsInfoBanner>
			<TypographyParagraph>
				{{ $t('settings.sub-networks.vrsSettingsForm.derivedSettingsHint3') }}
				<router-link :to="{ name: 'settings' }">{{ $t('settings.sub-networks.vrsSettingsForm.derivedSettingsLink') }}</router-link>
			</TypographyParagraph>
		</VerticalLayout>
	</Skeleton>
</template>

<script>
import { ref, watch, toRef, computed } from 'vue';
import { Skeleton, TypographyParagraph, TypographyText, Descriptions, DescriptionsItem, Tooltip, message } from 'ant-design-vue';
import { WarningTwoTone } from '@ant-design/icons-vue';

import Consts from '@/api/Consts';
import { useI18n } from 'vue-i18n';

import { useLicenseStore } from '@/stores/licenseStore';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import StoragePathTooltip from '@/components/common/StoragePathTooltip.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';

export default {
	components: {
		Skeleton, TypographyParagraph, TypographyText, Descriptions, DescriptionsItem, Tooltip,
		WarningTwoTone,
		LabeledSwitch, VerticalLayout, HorizontalLayout, StatusDisplay, PopconfirmButton, StoragePathTooltip, SettingsInfoBanner,
	},
	props: {
		loading: {
			type: Boolean,
			required: false,
		},
		settings: {
			type: Object,
			required: true,
		},
		onUpdateField: {
			type: Function,
			required: true,
		},
		cnetData: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		storagePath: {
			type: String,
			required: false,
			default: '',
		},
	},
	setup(props) {
		const { t } = useI18n();
		const subNetworkEnabled = ref(false);
		const vrsServiceEnabled = ref(false);
		const pppServiceEnabled = ref(false);
		const cssrBrdcDumpEnabled = ref(false);
		const dumpRestartRequired = ref(false);
		const lastSettingsId = ref(null);
		const settingsRef = toRef(props, 'settings');
		const cnetDataRef = toRef(props, 'cnetData');
		watch(settingsRef, value => {
			if (lastSettingsId.value !== value.id) {
				dumpRestartRequired.value = false;
				lastSettingsId.value = value.id;
			}
			subNetworkEnabled.value = value.status != 'stopped';
			vrsServiceEnabled.value = Boolean(value.ivrsEnabled);
			pppServiceEnabled.value = Boolean(value.cssrBrdcEnabled);
			cssrBrdcDumpEnabled.value = Boolean(value.cssrBrdcDumpEnabled);
		});
		const licenseStore = useLicenseStore();
		const licenseAllowVrs = computed(() => licenseStore.allowVrs);
		const licenseAllowPpp = computed(() => licenseStore.allowPppRtk);
		const finalNamesVrs = computed(() => {
			const mountpoints = settingsRef.value.rtcmfMountpoints || [];
			return mountpoints.map(item => settingsRef.value.finalNameVrs + '_' + item.label).join(', ');
		});
		const isPppRtkMode = computed(() => (
			licenseStore.allowPppGp && settingsRef.value.cssrGpEnabled
		));
		const finalNameCssrLabel = computed(() => (
			isPppRtkMode.value
				? t('settings.sub-networks.vrsSettingsForm.finalNameCssr')
				: t('settings.sub-networks.vrsSettingsForm.finalNameCssrPppAr')
		));
		const cssrBrdcDumpTooltipPath = computed(() => t(
			'settings.sub-networks.vrsSettingsForm.cssrBrdcDumpEnabledTooltip',
			{ label: settingsRef.value.label || '' },
		));
		const trafficStatusBadgeDict = ref({
			'disabled': 'default',
			'has_traffic': 'success',
			'no_traffic': 'error',
		});
		const canEnablePpp = computed(() => Boolean(cnetDataRef.value.allowToEnable));
		const onSubNetworkEnabledChange = value => {
			props.onUpdateField('status', value ? 'working' : 'stopped');
		};
		const onDumpChange = value => {
			dumpRestartRequired.value = true;
			props.onUpdateField('cssrBrdcDumpEnabled', value);
		};
		const onRestartConfirm = () => {
			props.onUpdateField('status', 'restarting', response => {
				if (response.status === 'ok') {
					dumpRestartRequired.value = false;
					message.success(t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.success'));
				}
			});
		};
		return {
			subNetworkEnabled,
			vrsServiceEnabled,
			pppServiceEnabled,
			cssrBrdcDumpEnabled,
			dumpRestartRequired,
			licenseAllowVrs,
			licenseAllowPpp,
			canEnablePpp,
			settingsRef,
			finalNamesVrs,
			finalNameCssrLabel,
			cssrBrdcDumpTooltipPath,
			trafficStatusBadgeDict,
			onSubNetworkEnabledChange,
			onDumpChange,
			onRestartConfirm,
			PINK: Consts.COLORS.pink,
		};
	},
}
</script>

<style scoped>
.dump-restart-button-hidden {
	visibility: hidden;
}
</style>
